import { validateAllVisitDrafts } from "./scheduleVisitPersistence";
import {
  buildPlannerSnapshotFromApiRow,
  mergePlannerRows,
} from "./scheduleOptionalSetupService";

const pick = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

function formatApiNumericField(value) {
  if (value === "" || value === null || value === undefined) return "";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value).trim();
}

export { formatApiNumericField };

export const VISIT_TYPE = {
  SCREENING: "screening",
  RESIDENTIAL: "residential",
  NON_RESIDENTIAL: "non_residential",
  ADHOC: "adhoc",
};

export const ADHOC_VISIT_KEY = "ADHOCVISIT";

export const COHORT_VISIT_COLORS = [
  "#0d9488",
  "#92400e",
  "#7c3aed",
  "#059669",
  "#b45309",
  "#4f46e5",
];

let visitDraftSeq = 0;

export function newVisitDraftId() {
  visitDraftSeq += 1;
  return `visit-draft-${Date.now()}-${visitDraftSeq}`;
}

export function calcLegacyPeriodDuration(start, finish) {
  const a = Number(start);
  const b = Number(finish);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return "";
  return String(b - a);
}

export function calcScreeningDuration(start, finish) {
  const a = Number(start);
  const b = Number(finish);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return "";
  return String(b - a + 1);
}

/** Cohort GET may omit ScreeningWindow* until SP 07 is deployed; accept save + legacy aliases. */
export function pickScreeningFieldsFromRow(row) {
  if (!row || typeof row !== "object") {
    return { screeningWindowStart: "", screeningWindowFinish: "", screeningDuration: "" };
  }
  const screeningWindowStart = formatApiNumericField(
    pick(
      row,
      "ScreeningWindowStart",
      "screeningWindowStart",
      "ScreeningStart",
      "screeningStart",
    ),
  );
  const screeningWindowFinish = formatApiNumericField(
    pick(
      row,
      "ScreeningWindowFinish",
      "screeningWindowFinish",
      "ScreeningEnd",
      "screeningEnd",
      "ScreeningFinish",
      "screeningFinish",
    ),
  );
  let screeningDuration = formatApiNumericField(
    pick(row, "ScreeningDuration", "screeningDuration"),
  );
  if (
    screeningDuration === "" &&
    screeningWindowStart !== "" &&
    screeningWindowFinish !== ""
  ) {
    screeningDuration = calcScreeningDuration(screeningWindowStart, screeningWindowFinish);
  }
  return { screeningWindowStart, screeningWindowFinish, screeningDuration };
}

export function hasScreeningWindowFields(row) {
  const fields = pickScreeningFieldsFromRow(row);
  return fields.screeningWindowStart !== "" || fields.screeningWindowFinish !== "";
}

function legacyVisitFields(overrides = {}) {
  return {
    visitOrder: overrides.visitOrder ?? overrides.visit ?? null,
    refVisitId: overrides.refVisitId ?? overrides.refVisitWindow ?? "",
    screeningWindowStart: overrides.screeningWindowStart ?? "",
    screeningWindowFinish: overrides.screeningWindowFinish ?? "",
    screeningDuration: overrides.screeningDuration ?? "",
    startPeriod: overrides.startPeriod ?? "",
    finishPeriod: overrides.finishPeriod ?? "",
    periodDuration: overrides.periodDuration ?? "",
    tolerencePeriod: overrides.tolerencePeriod ?? "",
    upperTolerenceLimit: overrides.upperTolerenceLimit ?? "",
    nonResidVisitOccur: overrides.nonResidVisitOccur ?? "",
    nonResidTolerencePeriod: overrides.nonResidTolerencePeriod ?? "",
    nonResidUpperTolerenceLimit: overrides.nonResidUpperTolerenceLimit ?? "",
    nonResidVisitTypeId: overrides.nonResidVisitTypeId ?? "",
  };
}

export function isAdhocVisit(visit) {
  if (!visit) return false;
  return !!(
    visit.isAdhoc ||
    visit.visitType === VISIT_TYPE.ADHOC ||
    visit.id === ADHOC_VISIT_KEY ||
    String(visit.visitName || "").trim().toLowerCase() === "adhoc visit"
  );
}

export function visitNameUnchanged(visit, newName, originalName = null) {
  const next = String(newName || "").trim();
  if (!next) return true;
  if (originalName != null && isSameVisitName(originalName, next)) return true;
  return isSameVisitName(visit?.visitName, next);
}

export function findDuplicateVisitName(visits, visit, newName) {
  const normalized = String(newName || "").trim().toLowerCase();
  if (!normalized || !visit) return null;
  const sameDbId = visit.visitsPerStudyId
    ? String(visit.visitsPerStudyId)
    : null;
  return (
    (visits || []).find((v) => {
      if (v.id === visit.id) return false;
      if (
        sameDbId &&
        v.visitsPerStudyId &&
        String(v.visitsPerStudyId) === sameDbId
      ) {
        return false;
      }
      return String(v.visitName || "").trim().toLowerCase() === normalized;
    }) || null
  );
}

/** Next default label from highest Visit N in use (Visit 1 + Visit 3 → Visit 4). */
export function nextDefaultVisitName(visits = []) {
  let max = 0;
  (visits || []).forEach((visit) => {
    const match = String(visit?.visitName || "")
      .trim()
      .match(/^visit\s*(\d+)$/i);
    if (match) max = Math.max(max, Number(match[1]));
  });
  return `Visit ${max + 1}`;
}

export function isSameVisitName(a, b) {
  return (
    String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase()
  );
}

/** Non-screening visits can be removed; without screening at least one regular visit must remain. */
export function canDeleteVisitFromList(visit, persistableVisits, studyScreening) {
  if (
    !visit ||
    isAdhocVisit(visit) ||
    visit.isScreening ||
    visit.visitType === VISIT_TYPE.SCREENING
  ) {
    return false;
  }
  const regularCount = persistableVisits.filter(
    (v) => v.visitType !== VISIT_TYPE.SCREENING && !v.isScreening,
  ).length;
  if (!studyScreening && regularCount <= 1) return false;
  return true;
}

export function getReorderableVisits(visits = []) {
  return visits.filter(
    (v) =>
      !isAdhocVisit(v) &&
      v.visitType !== VISIT_TYPE.SCREENING &&
      !v.isScreening,
  );
}

export function canMoveVisit(visits, visit, direction) {
  const reorderable = getReorderableVisits(visits);
  const index = reorderable.findIndex((v) => v.id === visit?.id);
  if (index < 0) return false;
  if (direction === "up") return index > 0;
  if (direction === "down") return index < reorderable.length - 1;
  return false;
}

export function createAdhocVisit(overrides = {}) {
  return {
    id: ADHOC_VISIT_KEY,
    visitsPerStudyId: null,
    visitName: "Adhoc Visit",
    visitType: VISIT_TYPE.ADHOC,
    isAdhoc: true,
    isScreening: false,
    locked: true,
    canDelete: false,
    canRename: false,
    ...legacyVisitFields(overrides),
  };
}

export function createScreeningVisit(overrides = {}) {
  const fields = legacyVisitFields(overrides);
  const screeningDuration =
    (overrides.screeningDuration !== undefined &&
    overrides.screeningDuration !== null &&
    String(overrides.screeningDuration).trim() !== ""
      ? String(overrides.screeningDuration).trim()
      : "") ||
    calcScreeningDuration(fields.screeningWindowStart, fields.screeningWindowFinish);

  return {
    id: overrides.id || newVisitDraftId(),
    visitsPerStudyId: overrides.visitsPerStudyId || null,
    visitName: overrides.visitName || "Screening",
    visitType: VISIT_TYPE.SCREENING,
    isScreening: true,
    locked: true,
    canDelete: false,
    canRename: true,
    ...fields,
    screeningDuration,
    plannerSnapshot: overrides.plannerSnapshot ?? null,
  };
}

export function createVisitDraft(overrides = {}) {
  if (isAdhocVisit(overrides)) {
    return createAdhocVisit(overrides);
  }

  const visitType =
    overrides.visitType ||
    (overrides.isScreening ? VISIT_TYPE.SCREENING : VISIT_TYPE.NON_RESIDENTIAL);

  if (visitType === VISIT_TYPE.SCREENING) {
    return createScreeningVisit(overrides);
  }

  if (visitType === VISIT_TYPE.ADHOC) {
    return createAdhocVisit(overrides);
  }

  const fields = legacyVisitFields(overrides);
  const periodDuration =
    overrides.periodDuration ||
    calcLegacyPeriodDuration(fields.startPeriod, fields.finishPeriod);

  return {
    id: overrides.id || newVisitDraftId(),
    visitsPerStudyId: overrides.visitsPerStudyId || null,
    visitName: overrides.visitName ?? "New Visit",
    visitType,
    isScreening: false,
    locked: false,
    canDelete: true,
    canRename: true,
    ...fields,
    periodDuration,
    plannerSnapshot: overrides.plannerSnapshot ?? null,
  };
}

export function visitsWithoutAdhoc(visits = []) {
  return (Array.isArray(visits) ? visits : []).filter((visit) => !isAdhocVisit(visit));
}

export function withAdhocVisit(visits = []) {
  const list = visitsWithoutAdhoc(visits).map((visit) => createVisitDraft(visit));
  return [...list, createAdhocVisit()];
}

function sameVisitRecord(a, b) {
  if (!a || !b) return false;
  if (a.id != null && b.id != null && String(a.id) === String(b.id)) return true;
  if (a.visitsPerStudyId && b.visitsPerStudyId) {
    return String(a.visitsPerStudyId) === String(b.visitsPerStudyId);
  }
  return false;
}

/** Legacy visit rail order — saved visits only, screening first, adhoc excluded. */
export function visitsForReferenceVisitRail(visits = []) {
  const rail = visitsForVisitRail(visits, { includeAdhoc: false });
  return [...rail].sort((a, b) => {
    const aScreen = a.visitType === VISIT_TYPE.SCREENING || a.isScreening;
    const bScreen = b.visitType === VISIT_TYPE.SCREENING || b.isScreening;
    if (aScreen !== bScreen) return aScreen ? -1 : 1;
    const av = Number(a.visitOrder ?? 999);
    const bv = Number(b.visitOrder ?? 999);
    if (Number.isFinite(av) && Number.isFinite(bv) && av !== bv) return av - bv;
    return String(a.visitName || "").localeCompare(String(b.visitName || ""));
  });
}

export function listPriorVisitsForReference(allVisits = [], currentVisit) {
  return buildReferenceVisitOptions(allVisits, currentVisit).options;
}

export function visitNeedsReferenceSelection(allVisits = [], currentVisit) {
  return buildReferenceVisitOptions(allVisits, currentVisit).show;
}

export function resolveReferenceVisitLabel(visit, allVisits = []) {
  const refId = String(visit?.refVisitId || "").trim();
  if (!refId) return "";
  const match = visitsForReferenceVisitRail(allVisits).find(
    (row) => String(row.visitsPerStudyId) === refId,
  );
  return match?.visitName ? String(match.visitName).trim() : "";
}

export function resolveRefVisitIdFromApiRow(row, candidateVisits = []) {
  const direct = pick(row, "RefVisitId", "refVisitId");
  if (direct != null && String(direct).trim() !== "") {
    return String(direct).trim();
  }
  const window = String(pick(row, "RefVisitWindow", "refVisitWindow") || "").trim();
  if (!window) return "";
  const upperWindow = window.toUpperCase();
  const byId = candidateVisits.find(
    (visit) => String(visit.visitsPerStudyId || "").toUpperCase() === upperWindow,
  );
  if (byId?.visitsPerStudyId) return String(byId.visitsPerStudyId);
  const byName = candidateVisits.find((visit) => isSameVisitName(visit.visitName, window));
  if (byName?.visitsPerStudyId) return String(byName.visitsPerStudyId);
  return "";
}

/** Match legacy ddVisitReference — prior saved visits in rail order. */
export function buildReferenceVisitOptions(allVisits = [], currentVisit) {
  if (!currentVisit || isAdhocVisit(currentVisit)) {
    return { show: false, options: [] };
  }

  const railVisits = visitsForReferenceVisitRail(allVisits);
  const currentIdx = railVisits.findIndex((visit) => sameVisitRecord(visit, currentVisit));
  if (currentIdx <= 0) {
    return { show: false, options: [] };
  }

  const options = railVisits
    .slice(0, currentIdx)
    .filter(
      (visit) =>
        visit.visitsPerStudyId &&
        !isAdhocVisit(visit) &&
        visit.visitType !== VISIT_TYPE.SCREENING &&
        !visit.isScreening,
    )
    .map((visit) => ({
      value: String(visit.visitsPerStudyId),
      label: visit.visitName || "Visit",
    }));

  return { show: options.length > 0, options };
}

/** Visit rail / editor list — persisted visits + Adhoc (UI-only, always last). */
export function visitsForVisitRail(visits = [], { includeAdhoc = true } = {}) {
  const list = (Array.isArray(visits) ? visits : [])
    .map((visit) => createVisitDraft(visit))
    .filter((visit) => !isAdhocVisit(visit))
    .filter((visit) => !!visit.visitsPerStudyId);

  const sorted = [...list].sort((a, b) => {
    const aScreen = a.visitType === VISIT_TYPE.SCREENING || a.isScreening;
    const bScreen = b.visitType === VISIT_TYPE.SCREENING || b.isScreening;
    if (aScreen !== bScreen) return aScreen ? -1 : 1;
    return 0;
  });

  return includeAdhoc ? withAdhocVisit(sorted) : sorted;
}

function visitTypeFromApiRow(row, isScreening) {
  if (isScreening) return VISIT_TYPE.SCREENING;
  const raw = String(pick(row, "VisitType", "visitType") || "").toLowerCase();
  if (raw === "residential") return VISIT_TYPE.RESIDENTIAL;
  return VISIT_TYPE.NON_RESIDENTIAL;
}

export function isScreeningVisitRow(row) {
  const visitNum = pick(row, "Visit", "visit");
  const name = String(pick(row, "VisitName", "visitName") || "").toLowerCase();
  return !!(
    pick(row, "IsStudyScreening", "isStudyScreening") === true ||
    pick(row, "IsStudyScreening", "isStudyScreening") === 1 ||
    visitNum === 0 ||
    visitNum === "0" ||
    visitNum === -1 ||
    visitNum === "-1" ||
    name === "screening"
  );
}

export function visitDraftFromApiRow(row, index = 0) {
  const isScreening = isScreeningVisitRow(row);
  const visitType = visitTypeFromApiRow(row, isScreening);
  const baseId = pick(row, "VisitsPerStudyId", "visitsPerStudyId") || newVisitDraftId();

  if (isScreening) {
    return createScreeningVisit({
      id: baseId,
      visitsPerStudyId: pick(row, "VisitsPerStudyId", "visitsPerStudyId") || null,
      ...pickScreeningFieldsFromRow(row),
      plannerSnapshot: buildPlannerSnapshotFromApiRow(row),
    });
  }

  const common = {
    id: baseId,
    visitsPerStudyId: pick(row, "VisitsPerStudyId", "visitsPerStudyId") || null,
    visitName: pick(row, "VisitName", "visitName") || `Visit ${index + 1}`,
    visitType,
    visitOrder: pick(row, "Visit", "visit") ?? null,
    refVisitId: pick(row, "RefVisitId", "refVisitId") ?? "",
    plannerSnapshot: buildPlannerSnapshotFromApiRow(row),
    startPeriod: formatApiNumericField(pick(row, "StartPeriod", "startPeriod")),
    finishPeriod: formatApiNumericField(pick(row, "FinishPeriod", "finishPeriod")),
    periodDuration: formatApiNumericField(pick(row, "PeriodDuration", "periodDuration")),
    tolerencePeriod: formatApiNumericField(pick(row, "TolerencePeriod", "tolerencePeriod")),
    upperTolerenceLimit:
      formatApiNumericField(pick(row, "UpperTolerenceLimit", "upperTolerenceLimit")),
    nonResidVisitOccur: formatApiNumericField(pick(row, "NonResidVisitOccur", "nonResidVisitOccur")),
    nonResidTolerencePeriod: formatApiNumericField(
      pick(row, "NonResidTolerencePeriod", "nonResidTolerencePeriod"),
    ),
    nonResidUpperTolerenceLimit:
      formatApiNumericField(
        pick(row, "NonResidUpperTolerenceLimit", "nonResidUpperTolerenceLimit"),
      ) ||
      formatApiNumericField(pick(row, "UpperTolerenceLimit", "upperTolerenceLimit")) ||
      "",
    nonResidVisitTypeId:
      pick(row, "TypeOfNonResidVisitIdRef", "typeOfNonResidVisitIdRef") ?? "",
  };

  return createVisitDraft(common);
}

function sortApiVisitRows(rows = []) {
  return [...rows].sort((a, b) => {
    const aScreen = isScreeningVisitRow(a);
    const bScreen = isScreeningVisitRow(b);
    if (aScreen !== bScreen) return aScreen ? -1 : 1;
    const av = Number(pick(a, "Visit", "visit"));
    const bv = Number(pick(b, "Visit", "visit"));
    if (Number.isFinite(av) && Number.isFinite(bv) && av !== bv) return av - bv;
    const an = String(pick(a, "VisitName", "visitName") || "");
    const bn = String(pick(b, "VisitName", "visitName") || "");
    return an.localeCompare(bn);
  });
}

export function visitsFromApiRows(rows = [], { excludeSpecial = true } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const filtered = excludeSpecial
    ? list.filter((row) => {
        if (isStudyScreeningVisitRow(row) || isScreeningVisitRow(row)) return true;
        const visitNum = Number(pick(row, "Visit", "visit"));
        return !Number.isFinite(visitNum) || (visitNum >= 0 && visitNum !== -2);
      })
    : list;

  if (!filtered.length) return [];

  const sorted = sortApiVisitRows(filtered);
  const drafts = sorted.map((row, index) => visitDraftFromApiRow(row, index));

  return drafts.map((draft, index) => {
    const resolved = resolveRefVisitIdFromApiRow(sorted[index], drafts.slice(0, index));
    if (!resolved) return draft;
    return createVisitDraft({ ...draft, refVisitId: resolved });
  });
}

/** Study screening group (GroupNo 0): screening visit + adhoc only — no treatment visits. */
export function normalizeStudyScreeningGroupVisits(existing = []) {
  const list = Array.isArray(existing) ? existing.map((v) => createVisitDraft(v)) : [];
  const withoutAdhoc = list.filter((visit) => !isAdhocVisit(visit));
  const screeningVisit =
    withoutAdhoc.find(
      (v) => v.visitType === VISIT_TYPE.SCREENING || v.isScreening,
    ) || createScreeningVisit();
  return withAdhocVisit([createScreeningVisit(screeningVisit)]);
}

export function treatmentVisitsFromApiRows(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((row) => {
    const visitNum = Number(pick(row, "Visit", "visit"));
    if (Number.isFinite(visitNum) && (visitNum === -1 || visitNum === -2)) {
      return false;
    }
    if (isStudyScreeningVisitRow(row)) return false;
    return true;
  });
}

export function resolveSharedTreatmentVisitDrafts({
  visitDrafts = [],
  groupVisitDrafts = {},
  apiVisits = [],
  primaryGroupId = null,
} = {}) {
  const fromDrafts = visitsForVisitRail(visitDrafts);
  if (fromDrafts.length) {
    return fromDrafts;
  }
  if (primaryGroupId && groupVisitDrafts[primaryGroupId]?.length) {
    const fromGroup = visitsForVisitRail(groupVisitDrafts[primaryGroupId]);
    if (fromGroup.length) return fromGroup;
  }
  const apiRows = treatmentVisitsFromApiRows(apiVisits);
  if (primaryGroupId) {
    const scoped = apiRows.filter((row) => {
      const gid =
        pick(row, "GroupNameIdRef", "groupNameIdRef") ||
        pick(row, "GroupNameId", "groupNameId");
      return !gid || String(gid) === String(primaryGroupId);
    });
    if (scoped.length) {
      return visitsForVisitRail(
        scoped.map((row, index) => visitDraftFromApiRow(row, index)),
      );
    }
  }
  return visitsForVisitRail(
    apiRows.map((row, index) => visitDraftFromApiRow(row, index)),
  );
}

export function groupVisitsFromApi(rows = [], groupNames = []) {
  const byGroup = {};
  const names = Array.isArray(groupNames) ? groupNames : [];

  names.forEach((group, index) => {
    const groupId =
      pick(group, "GroupNameId", "groupNameId") ||
      pick(group, "GroupNameIdRef", "groupNameIdRef") ||
      `group-${index}`;
    byGroup[groupId] = [];
  });

  const list = Array.isArray(rows) ? rows : [];
  const groupedRows = {};
  list.forEach((row) => {
    if (isStudyScreeningVisitRow(row)) {
      const screeningGroup = names.find(
        (group) => Number(pick(group, "GroupNo", "groupNo")) === 0,
      );
      const screeningGroupId =
        pick(screeningGroup, "GroupNameId", "groupNameId") ||
        pick(screeningGroup, "GroupNameIdRef", "groupNameIdRef") ||
        resolveStudyScreeningGroupId(rows, names);
      if (screeningGroupId) {
        if (!groupedRows[screeningGroupId]) groupedRows[screeningGroupId] = [];
        groupedRows[screeningGroupId].push(row);
      }
      return;
    }
    const visitNum = Number(pick(row, "Visit", "visit"));
    if (Number.isFinite(visitNum) && visitNum === -2) {
      return;
    }
    const groupId =
      pick(row, "GroupNameIdRef", "groupNameIdRef") ||
      pick(row, "GroupNameId", "groupNameId");
    const isTreatmentGroup = names.some(
      (group) =>
        Number(pick(group, "GroupNo", "groupNo")) !== 0 &&
        String(pick(group, "GroupNameId", "groupNameId") || pick(group, "GroupNameIdRef")) ===
          String(groupId),
    );
    const key =
      groupId && byGroup[groupId] && isTreatmentGroup
        ? groupId
        : treatmentGroupNames(names)[0]
          ? pick(treatmentGroupNames(names)[0], "GroupNameId", "groupNameId") ||
            pick(treatmentGroupNames(names)[0], "GroupNameIdRef", "groupNameIdRef")
          : Object.keys(byGroup)[0];
    if (!key) return;
    if (!groupedRows[key]) groupedRows[key] = [];
    groupedRows[key].push(row);
  });

  Object.entries(groupedRows).forEach(([key, groupRows]) => {
    const groupMeta = names.find(
      (group) =>
        String(pick(group, "GroupNameId", "groupNameId") || pick(group, "GroupNameIdRef")) ===
        String(key),
    );
    const isScreeningGroup = Number(pick(groupMeta, "GroupNo", "groupNo")) === 0;
    sortApiVisitRows(groupRows).forEach((row, index) => {
      if (!byGroup[key]) byGroup[key] = [];
      byGroup[key].push(visitDraftFromApiRow(row, index));
    });
    if (isScreeningGroup) {
      byGroup[key] = visitsForVisitRail(byGroup[key]);
    }
  });

  return byGroup;
}

/** Align with legacy SP: screening is always first; adhoc is always last (UI-only). */
export function normalizeVisitDrafts(
  existing = [],
  { screening = false, includeAdhoc = true } = {},
) {
  const list = Array.isArray(existing) ? existing.map((v) => createVisitDraft(v)) : [];
  const withoutAdhoc = list.filter((visit) => !isAdhocVisit(visit));
  const regular = withoutAdhoc.filter(
    (v) => v.visitType !== VISIT_TYPE.SCREENING && !v.isScreening,
  );

  let core = [];
  if (!screening) {
    core = regular.length
      ? regular
      : [createVisitDraft({ visitName: "Visit 1", visitType: VISIT_TYPE.NON_RESIDENTIAL })];
  } else {
    const screeningVisit =
      withoutAdhoc.find((v) => v.visitType === VISIT_TYPE.SCREENING || v.isScreening) ||
      createScreeningVisit();
    const regularVisits = regular.length
      ? regular
      : [createVisitDraft({ visitName: "Visit 1", visitType: VISIT_TYPE.NON_RESIDENTIAL })];
    core = [createScreeningVisit(screeningVisit), ...regularVisits];
  }

  return includeAdhoc ? withAdhocVisit(core) : core;
}

export function countVisitsForApi(visits = [], { screening = false } = {}) {
  return visitsWithoutAdhoc(normalizeVisitDrafts(visits, { screening })).length;
}

export function validateVisitDrafts(visits = [], { screening = false } = {}) {
  const list = visitsWithoutAdhoc(normalizeVisitDrafts(visits, { screening }));
  if (!list.length) {
    return { valid: false, message: "Add at least one visit." };
  }
  if (list.length > 99) {
    return { valid: false, message: "Maximum 99 visits allowed." };
  }

  const editable = list.filter(
    (v) => v.visitType !== VISIT_TYPE.SCREENING && !v.isScreening && !isAdhocVisit(v),
  );
  const emptyName = editable.find((v) => !String(v.visitName || "").trim());
  if (emptyName) {
    return { valid: false, message: "Every visit needs a name." };
  }

  if (screening) {
    const first = list[0];
    if (first.visitType !== VISIT_TYPE.SCREENING) {
      return { valid: false, message: "Screening must be the first visit when enabled." };
    }
  }

  const detailCheck = validateAllVisitDrafts(list, { screening });
  if (!detailCheck.valid) return detailCheck;

  return { valid: true, value: list, count: list.length };
}

export function pickGroupNameId(source) {
  return (
    pick(source, "GroupNameId", "groupNameId", "GroupNameIdRef", "groupNameIdRef", "id") ||
    null
  );
}

export function resolveScheduleGroupId({ groupId, visit, cohorts = [] } = {}) {
  if (groupId) return groupId;
  const fromVisit = pickGroupNameId(visit);
  if (fromVisit) return fromVisit;
  return pickGroupNameId(cohorts[0]) || null;
}

export function resolveStudyScreeningGroupId(visits = [], groupNames = []) {
  const fromGroup = (groupNames || []).find(
    (group) => Number(pick(group, "GroupNo", "groupNo")) === 0,
  );
  if (fromGroup) {
    return (
      pick(fromGroup, "GroupNameId", "groupNameId") ||
      pick(fromGroup, "GroupNameIdRef", "groupNameIdRef") ||
      null
    );
  }

  const screeningRow = (visits || []).find((row) => {
    const visitNum = Number(pick(row, "Visit", "visit"));
    return (
      visitNum === -1 ||
      pick(row, "IsStudyScreening", "isStudyScreening") === true ||
      pick(row, "IsStudyScreening", "isStudyScreening") === 1
    );
  });
  return (
    pick(screeningRow, "GroupNameIdRef", "groupNameIdRef") ||
    pick(screeningRow, "GroupNameId", "groupNameId") ||
    null
  );
}

export function isStudyScreeningVisitRow(row) {
  const visitNum = Number(pick(row, "Visit", "visit"));
  return (
    visitNum === -1 ||
    pick(row, "IsStudyScreening", "isStudyScreening") === true ||
    pick(row, "IsStudyScreening", "isStudyScreening") === 1
  );
}

export function studyScreeningVisitsFromApi(rows = [], groupId = null) {
  const list = (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!isStudyScreeningVisitRow(row)) return false;
    const rowGroupId =
      pick(row, "GroupNameIdRef", "groupNameIdRef") ||
      pick(row, "GroupNameId", "groupNameId");
    return !groupId || !rowGroupId || String(rowGroupId) === String(groupId);
  });
  if (!list.length) return [];
  return sortApiVisitRows(list).map((row, index) => visitDraftFromApiRow(row, index));
}

export function treatmentGroupNames(groupNames = []) {
  return (Array.isArray(groupNames) ? groupNames : []).filter(
    (group) => Number(pick(group, "GroupNo", "groupNo")) !== 0,
  );
}

export function augmentGroupNamesWithStudyScreening(
  groupNames = [],
  visits = [],
  studyGroup = {},
) {
  const screeningEnabled = !!(
    studyGroup?.StudyScreening ??
    studyGroup?.studyScreening
  );
  if (!screeningEnabled) return groupNames;

  const names = Array.isArray(groupNames) ? groupNames : [];
  if (names.some((group) => Number(pick(group, "GroupNo", "groupNo")) === 0)) {
    return names;
  }

  const groupId = resolveStudyScreeningGroupId(visits, names);
  if (!groupId) return names;

  const primaryTreatment =
    names.find((group) => Number(pick(group, "GroupNo", "groupNo")) === 1) ||
    treatmentGroupNames(names)[0] ||
    {};

  const screeningSrt = !!(
    pick(studyGroup, "StudyScreeningRandomisationTogether", "studyScreeningRandomisationTogether") ||
    pick(primaryTreatment, "ScreeningrandomisationTogether", "screeningrandomisationTogether") ||
    pick(
      names.find((group) => Number(pick(group, "GroupNo", "groupNo")) === 0),
      "ScreeningrandomisationTogether",
      "screeningrandomisationTogether",
    )
  );

  return [
    {
      GroupNameId: groupId,
      GroupName: "Study Screening",
      GroupNo: 0,
      GroupScreening: 0,
      ScreeningrandomisationTogether: screeningSrt,
    },
    ...names,
  ];
}

export function buildMultiGroupVisitCohorts({
  groupNames = [],
  groupVisitDrafts = {},
  visitDrafts = [],
  apiVisits = [],
  studyScreening = false,
  allGroupsSameSchedule = true,
}) {
  const augmentedNames = augmentGroupNamesWithStudyScreening(
    groupNames,
    apiVisits,
    { StudyScreening: studyScreening },
  );
  const treatmentGroups = treatmentGroupNames(augmentedNames);
  const cohorts = [];

  if (studyScreening) {
    const screeningGroupId =
      resolveStudyScreeningGroupId(apiVisits, augmentedNames) || "study-screening";
    const draftRows = groupVisitDrafts[screeningGroupId];
    const apiRows = studyScreeningVisitsFromApi(apiVisits, screeningGroupId);
    cohorts.push({
      id: screeningGroupId,
      name: "Study Screening",
      color: "#6366f1",
      visits: visitsForVisitRail(
        draftRows?.length ? draftRows : apiRows,
      ),
      isStudyScreeningGroup: true,
    });
  }

  const primaryGroup =
    treatmentGroups.find(
      (group) => Number(pick(group, "GroupNo", "groupNo")) === 1,
    ) || treatmentGroups[0];
  const primaryGroupId =
    pick(primaryGroup, "GroupNameId", "groupNameId") ||
    pick(primaryGroup, "GroupNameIdRef", "groupNameIdRef") ||
    null;
  const sharedTreatmentVisits = allGroupsSameSchedule
    ? resolveSharedTreatmentVisitDrafts({
        visitDrafts,
        groupVisitDrafts,
        apiVisits,
        primaryGroupId,
      })
    : null;

  treatmentGroups.forEach((group, index) => {
    const id =
      pick(group, "GroupNameId", "groupNameId") ||
      pick(group, "GroupNameIdRef", "groupNameIdRef") ||
      `group-${index}`;
    const source = allGroupsSameSchedule
      ? sharedTreatmentVisits
      : groupVisitDrafts[id]?.length
        ? groupVisitDrafts[id]
        : treatmentVisitsFromApiRows(apiVisits)
            .filter((row) => {
              const gid =
                pick(row, "GroupNameIdRef", "groupNameIdRef") ||
                pick(row, "GroupNameId", "groupNameId");
              return !gid || String(gid) === String(id);
            })
            .map((row, rowIndex) => visitDraftFromApiRow(row, rowIndex));
    cohorts.push({
      id,
      name: pick(group, "GroupName", "groupName") || `Group ${index + 1}`,
      color: COHORT_VISIT_COLORS[index % COHORT_VISIT_COLORS.length],
      visits: visitsForVisitRail(source || []),
      isSharedSchedule: allGroupsSameSchedule,
      isTreatmentGroup: true,
    });
  });

  return cohorts;
}

/** Dropdown options: Study Screening + individual treatment groups. */
export function cohortsForGroupDropdown(cohorts = []) {
  return Array.isArray(cohorts) ? cohorts : [];
}

export function cohortVisitGroups(groupNames = [], groupVisitDrafts = {}, screening = false) {
  const names = treatmentGroupNames(groupNames);
  return names.map((group, index) => {
    const id =
      pick(group, "GroupNameId", "groupNameId") ||
      pick(group, "GroupNameIdRef", "groupNameIdRef") ||
      `group-${index}`;
    const visits = visitsForVisitRail(groupVisitDrafts[id]);
    return {
      id,
      name: pick(group, "GroupName", "groupName") || `Cohort ${String.fromCharCode(65 + index)}`,
      color: COHORT_VISIT_COLORS[index % COHORT_VISIT_COLORS.length],
      visits,
    };
  });
}

/** @deprecated use normalizeVisitDrafts */
export function ensureVisitDrafts(existing = [], options = {}) {
  return normalizeVisitDrafts(existing, options);
}

/** @deprecated use normalizeVisitDrafts */
export function seedVisitDrafts({ screening = false } = {}) {
  return normalizeVisitDrafts([], { screening });
}

function matchesVisitRef(visit, visitRef) {
  const ref = String(visitRef ?? "");
  if (!ref || !visit) return false;
  return String(visit.id) === ref || String(visit.visitsPerStudyId) === ref;
}

export function patchPlannerSnapshotOnVisit(visit, patch) {
  if (!visit || !patch) return visit;
  return {
    ...visit,
    plannerSnapshot: mergePlannerRows(visit.plannerSnapshot, patch),
  };
}

export function patchPlannerSnapshotInVisits(visits = [], visitRef, patch) {
  if (!visitRef || !patch) return visits;
  return visits.map((visit) =>
    matchesVisitRef(visit, visitRef)
      ? patchPlannerSnapshotOnVisit(visit, patch)
      : visit,
  );
}
